/**
 * What a priority band is called, and what one is not.
 *
 * Its own file so the interface can reach it without reaching the arithmetic
 * that produces it. Four strings and a note: the labels are a reading order,
 * and saying so beside them is not decoration.
 *
 * THE FRAMEWORK'S OWN WORDS. Section 8.2 names the user-facing priorities
 * "Review now", "Review next", "Monitor" and "No current action identified",
 * and the demo scenario's expected results are written in them. They were
 * softened to "Start here", "Then this", "Worth keeping an eye on" and "Nothing
 * to do right now", to keep the board reading as an order and not a directive,
 * and that cost more than it bought: a screen compared with the document it is
 * judged against did not use its words, "Then this" has no referent, and
 * "Nothing to do right now" claims more than the engine knows. "No current
 * action identified" says what was true: none was found in the answers given.
 *
 * What keeps the order from reading as an instruction is the note below, which
 * is printed wherever a band is, and not the choice of verb. "Review" is about
 * looking at something, which is also what the controlled language allows
 * ("consider reviewing this with a licensed adviser").
 *
 * Asvise's privacy policy states that the platform's functions are not intended
 * to make a decision producing legal or similarly significant effects, and its
 * adviser terms call words like "gap" and "comparison" organisational
 * indicators only. "Review now" has to be read in that light or the product
 * contradicts something the company has already published.
 */
export type Band = 'REVIEW_NOW' | 'REVIEW_NEXT' | 'MONITOR' | 'NONE'

export const BAND_LABEL: Record<Band, string> = {
  REVIEW_NOW: 'Review now',
  REVIEW_NEXT: 'Review next',
  MONITOR: 'Monitor',
  NONE: 'No current action identified',
}

/**
 * Printed wherever a band is, because a band without it reads as an
 * instruction.
 */
export const BAND_NOTE =
  'This is the order we suggest reading in, not advice about what to buy. Nothing here says a policy is unnecessary or unsuitable, and no product has been compared or recommended.'
